// Global Instructions Rule Applied!
//
// Legal rights models — the whole-view payload from
// GET /deal-execution/legal-rights (view__legal_rights): agreement sets with
// nested rights (each carrying its live evaluation from
// legal__evaluate_rights), the cap-table position the evaluator used, and the
// cfg catalogs / pickers the capture UI needs. Kept separate from
// cap_table_model.ts (same isolation rationale).
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

// Arithmetic of a rule evaluation (evaluator output, display-only).
export const LegalRuleArithmeticSchema = z
  .object({
    variable: z.string().nullish(),
    variable_label: z.string().nullish(),
    unit: z.string().nullish(),
    value: z.number().nullish(),
    operator: z.string().nullish(),
    threshold: z.number().nullish(),
    margin: z.number().nullish(),
    our_units: z.number().nullish(),
    fd_total: z.number().nullish(),
    issued_total: z.number().nullish(),
    cap_table_versions__id: z.string().nullish(),
    as_of_date: z.string().nullish()
  })
  .strict()

export type LegalRuleArithmetic = z.infer<typeof LegalRuleArithmeticSchema>

// Reference rights wrap the terminal right's arithmetic.
export const LegalReferenceArithmeticSchema = z
  .object({
    referenced_right_id: z.string().nullish(),
    referenced_right_name: z.string().nullish(),
    referenced: LegalRuleArithmeticSchema.nullish()
  })
  .strict()

export type LegalReferenceArithmetic = z.infer<typeof LegalReferenceArithmeticSchema>

export const LegalEvaluationSchema = z
  .object({
    status: z.string().nullish(),
    reason: z.string().nullish(),
    value: z.number().nullish(),
    arithmetic: z.union([LegalReferenceArithmeticSchema, LegalRuleArithmeticSchema]).nullish()
  })
  .strict()

export type LegalEvaluation = z.infer<typeof LegalEvaluationSchema>

export const LegalRightSchema = z
  .object({
    legal_rights__id: z.string(),
    legal_rights__name: z.string(),
    legal_rights__basis: z.string(),
    legal_rights__rule_variable: z.string().nullish(),
    legal_rights__rule_operator: z.string().nullish(),
    legal_rights__rule_threshold: z.number().nullish(),
    legal_rights__referenced_right_id: z.string().nullish(),
    referenced_right_name: z.string().nullish(),
    legal_rights__reference_text: z.string().nullish(),
    legal_rights__carried: z.boolean(),
    legal_rights__effective_from: z.string().nullish(),
    legal_rights__end_date: z.string().nullish(),
    legal_rights__section_ref: z.string().nullish(),
    legal_rights__source_url: z.string().nullish(),
    legal_rights__notes: z.string().nullish(),
    legal_rights__sort_order: z.number().nullish(),
    legal_right_types__id: z.string().nullish(),
    legal_right_types__label: z.string().nullish(),
    file_urls_deals__id: z.string().nullish(),
    file_urls_deals__file_name: z.string().nullish(),
    file_urls_deals__type: z.string().nullish(),
    file_urls_deals__deal_id: z.string().nullish(),
    evaluation: LegalEvaluationSchema
  })
  .strict()

export type LegalRight = z.infer<typeof LegalRightSchema>

export const LegalAgreementSetSchema = z
  .object({
    legal_agreement_sets__id: z.string(),
    legal_agreement_sets__name: z.string(),
    legal_agreement_sets__status: z.string(),
    legal_agreement_sets__executed_date: z.string().nullish(),
    legal_agreement_sets__superseded_date: z.string().nullish(),
    legal_agreement_sets__superseded_by_id: z.string().nullish(),
    superseded_by_name: z.string().nullish(),
    legal_agreement_sets__notes: z.string().nullish(),
    legal_agreement_sets__date_modified: z.string().nullish(),
    rounds__id: z.string().nullish(),
    rounds__name: z.string().nullish(),
    deals__id: z.string().nullish(),
    deals__name: z.string().nullish(),
    rights: z.array(LegalRightSchema)
  })
  .strict()

export type LegalAgreementSet = z.infer<typeof LegalAgreementSetSchema>

export const LegalPositionSchema = z
  .object({
    cap_table_versions__id: z.string().nullish(),
    cap_table_versions__status: z.string().nullish(),
    as_of_date: z.string().nullish(),
    our_units: z.number().nullish(),
    fd_total: z.number().nullish(),
    issued_total: z.number().nullish(),
    our_pct_fd: z.number().nullish(),
    our_pct_issued: z.number().nullish(),
    at_risk_margin: z.number().nullish(),
    // Booked splits dated after the evaluated version's as-of date — the
    // denominator is stale and percentages are inflated until restated.
    stale_splits: z
      .array(
        z
          .object({
            asset_transactions__transaction_date: z.string(),
            asset_transactions__split_factor: z.number().nullish()
          })
          .strict()
      )
      .optional()
      .default([])
  })
  .strict()

export type LegalPosition = z.infer<typeof LegalPositionSchema>

export const LegalRightTypeOptionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    default_basis: z.string().nullish()
  })
  .strict()

export type LegalRightTypeOption = z.infer<typeof LegalRightTypeOptionSchema>

export const LegalRuleVariableSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    unit: z.string().nullish(),
    description: z.string().nullish()
  })
  .strict()

export type LegalRuleVariable = z.infer<typeof LegalRuleVariableSchema>

export const LegalCompanyRoundSchema = z
  .object({
    rounds__id: z.string(),
    rounds__name: z.string().nullish(),
    rounds__round_close_date: z.string().nullish()
  })
  .strict()

export type LegalCompanyRound = z.infer<typeof LegalCompanyRoundSchema>

export const LegalCompanyDealSchema = z
  .object({
    deals__id: z.string(),
    deals__name: z.string().nullish(),
    rounds__id: z.string().nullish()
  })
  .strict()

export type LegalCompanyDeal = z.infer<typeof LegalCompanyDealSchema>

export const LegalCompanyDocumentSchema = z
  .object({
    file_urls_deals__id: z.string(),
    file_urls_deals__file_name: z.string().nullish(),
    file_urls_deals__type: z.string().nullish(),
    file_urls_deals__lifecycle: z.string().nullish(),
    document_types__label: z.string().nullish(),
    deals__id: z.string().nullish(),
    deals__name: z.string().nullish()
  })
  .strict()

export type LegalCompanyDocument = z.infer<typeof LegalCompanyDocumentSchema>

export const LegalRightsDataSchema = z
  .object({
    sets: z.array(LegalAgreementSetSchema),
    position: LegalPositionSchema,
    right_types: z.array(LegalRightTypeOptionSchema),
    rule_variables: z.array(LegalRuleVariableSchema),
    company_rounds: z.array(LegalCompanyRoundSchema),
    company_deals: z.array(LegalCompanyDealSchema),
    company_documents: z.array(LegalCompanyDocumentSchema)
  })
  .strict()

export type LegalRightsData = z.infer<typeof LegalRightsDataSchema>

export function validateLegalRightsData(data: unknown): LegalRightsData {
  try {
    return validateWithZodSchema(LegalRightsDataSchema, data, 'legal rights') as LegalRightsData
  } catch (e) {
    throw new Error(`Legal rights validation failed: ${e}`)
  }
}
